import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

type HookProps = {
  clientToken: string;
  site: string;
  env: string;
  service: string;
  applicationId: string;
  version?: string;
  /**
   * Session replay privacy. Unset, the SDK's own default applies ('mask-user-input' in v4,
   * 'mask' from v5), which masks form inputs but may record on-screen text, so pages that
   * echo card data as text (card previews) must pass 'mask'.
   */
  defaultPrivacyLevel?: 'allow' | 'mask' | 'mask-user-input';
  /** Percentage of sessions recorded for replay (default 100). */
  sessionReplaySampleRate?: number;
};

type ReturnTuple = [
  (user: { [id: string]: string }) => void,
  () => void,
  (service: string) => void,
  (message: string, type: 'info' | 'warn' | 'error', context: object, error?: Error) => void,
];

function useDatadog({
  applicationId,
  clientToken,
  site,
  env,
  service,
  version,
  defaultPrivacyLevel,
  sessionReplaySampleRate = 100,
}: HookProps): ReturnTuple {
  console.group('datadog env:', env);

  if (env !== undefined && env !== 'undefined') {
    console.info({ applicationId, clientToken, site, service, version });

    datadogLogs.init({
      clientToken,
      site,
      env,
      service,
      forwardErrorsToLogs: true,
      silentMultipleInit: true,
      sampleRate: 100,
      version,
    });

    datadogRum.init({
      applicationId,
      clientToken,
      site,
      env,
      service,
      trackResources: true,
      trackLongTasks: true,
      sessionSampleRate: 100,
      sessionReplaySampleRate,
      defaultPrivacyLevel,
      silentMultipleInit: true,
      sampleRate: 100,
      trackUserInteractions: true,
      trackFrustrations: true,
      version,
    });

    datadogRum.startSessionReplayRecording();

    console.groupEnd();

    return [
      (user) => {
        datadogLogs.setUser(user);
        datadogRum.setUser(user);
      },
      () => {
        datadogLogs.clearUser();
        datadogRum.clearUser();
      },
      (newService) => {
        datadogLogs.setGlobalContextProperty('service', newService);
        datadogRum.setGlobalContextProperty('service', newService);
      },
      (message, type, context, error) => {
        if (type === 'info') {
          datadogLogs.logger.info(message, context, error);
        } else if (type === 'warn') {
          datadogLogs.logger.warn(message, context, error);
        } else if (type === 'error') {
          datadogLogs.logger.error(message, context, error);
        }
      },
    ];
  }

  console.groupEnd();

  return [() => {}, () => {}, () => {}, () => {}];
}

export default useDatadog;
